import { Component, OnInit, ViewChild } from "@angular/core"
import { MediaChange, ObservableMedia } from "@angular/flex-layout"
import { NgForm } from "@angular/forms"
import { MdDialog } from "@angular/material"
import { ActivatedRoute, Params, Router } from "@angular/router"
import { Subscription } from "rxjs/Subscription"

import { DisplayService } from "../../display.service"
import { Merchant } from "../../shared/merchant"
import { MerchantImageUrl } from "../../shared/merchant-image-url"
import { MerchantReferralCampaignPages, MerchantReferralCampaignPagesTemplate } from "../../shared/merchant-referral"
import { ToolService } from "../../tool.service"
import { AdvocateInfo } from "../advocate-info"
import { AdvocateReferralForm } from "../advocate-referral-form"
import { ReferralSummary } from "../referral"
import { ReferralService } from "../referral.service"
import { ReferralMagicEmailModalComponent } from "./email-modal/email-modal.component"
import { ReferralMagicShareModalComponent } from "./share-modal/share-modal.component"

@Component({
  selector: "app-merchant-advocate",
  templateUrl: "./merchant-advocate.component.html",
  styleUrls: ["./merchant-advocate.component.scss"]
})
export class ReferralMagicMerchantAdvocateComponent implements OnInit {
  // Route variables
  merchant_slug: string
  advocate_identifier: string
  token: string

  // Local variables
  advocate_friend_referred: boolean
  advocate_has_no_reward: boolean
  advocate_info: AdvocateInfo
  advocate_url: string
  cover_image: MerchantImageUrl
  error: string
  facebook_url: string
  friend_has_no_reward: boolean
  hide_text_content: boolean
  is_enabled: boolean
  is_navigator_share_enabled = navigator.share !== undefined
  is_small_device: boolean
  is_sms_enabled: boolean
  merchant: Merchant
  model: AdvocateReferralForm = {
    name: "",
    email: ""
  }
  pages: MerchantReferralCampaignPages
  promo_code: string
  referred_friend_name: string
  template_type: string
  template_class: string
  template: MerchantReferralCampaignPagesTemplate
  summary: ReferralSummary
  watcher: Subscription
  patterns: Object

  constructor(
    private displayService: DisplayService,
    private mdDialog: MdDialog,
    private referralService: ReferralService,
    private toolService: ToolService,
    private route: ActivatedRoute,
    private router: Router,
    private media: ObservableMedia
  ) {
    this.is_small_device = media.isActive("xs") || media.isActive("sm")
    this.watcher = media.subscribe((change: MediaChange) => {
      this.is_small_device = change ? change.mqAlias === "xs" || change.mqAlias === "sm" : false
    })
    this.is_sms_enabled = this.toolService.isSMSEnabled()
  }

  ngOnInit() {
    this.route.queryParams.forEach((params: Params) => {
      this.displayService.scrollToTop()
      this.displayService.setEmbedded(params.hasOwnProperty("embed"))
      this.displayService.setModal(params.hasOwnProperty("modal"))
      this.patterns = {
        email: this.toolService.EMAIL_VALIDATION_REGEX,
        phone: this.toolService.PHONE_VALIDATION_REGEX
      }
      if (params["name"]) {
        this.model.name = params["name"]
      }

      if (params["email"]) {
        this.model.email = params["email"]
      }

      if (params["mobile"]) {
        this.model.mobile = params["mobile"]
      }

      if (params["i"]) {
        this.model.invite_id = params["i"]
      }
    })
    this.route.params.forEach((params: Params) => {
      this.merchant_slug = params["merchant_slug"]
      this.advocate_identifier = params["advocate_identifier"]
      this.token = params["token"]

      this.referralService
        .getMerchant(this.merchant_slug)
        .then((merchant) => {
          this.merchant = merchant
          if (
            merchant.settings.referral_magic &&
            merchant.settings.referral_magic.enabled &&
            merchant.referral_program
          ) {
            if (this.advocate_identifier.indexOf(this.merchant.reference_prefix) === 0) {
              this.template_type = "advocate_sharing"
              this.template_class = "advocate-sharing"

              this.toolService.setItemInLocalStorage(`${this.merchant_slug}:advocate_code`, this.advocate_identifier)

              if (this.token) {
                this.toolService.setItemInLocalStorage(`${this.merchant_slug}:advocate_authorization_token`, this.token)
              } else {
                this.token = this.toolService.getItemFromLocalStorage(
                  `${this.merchant_slug}:advocate_authorization_token`
                )
              }
            } else {
              this.template_type = "friend_signup"
              this.template_class = "friend-signup"
            }

            this.pages = this.merchant.referral_program.pages
            this.is_enabled = true
            this.advocate_has_no_reward =
              !merchant.referral_program.advocate_reward || merchant.referral_program.advocate_reward.type === "none"
            this.friend_has_no_reward =
              !merchant.referral_program.referred_reward || merchant.referral_program.referred_reward.type === "none"
            this.displayService.setReferralMagicPageStyles(this.pages)

            return this.referralService
              .getAdvocateInfo(this.merchant_slug, this.advocate_identifier)
              .then((advocate_info) => {
                this.advocate_info = advocate_info
                this.advocate_url = `${window.location.protocol}//${window.location.host}/${this.merchant_slug}/${advocate_info.referrer_token}`
                this._setTemplate(this.template_type)

                if (this.template_type === "friend_signup" && this.model.invite_id) {
                  return this.referralService
                    .getFriendInvite(this.merchant.id, this.advocate_identifier, this.model.invite_id)
                    .then((referral_invite) => {
                      this.model.name = referral_invite.customer.name
                      switch (referral_invite.customer.contact_type) {
                        case "email":
                          this.model.email = referral_invite.customer.contact
                          break
                        case "phone":
                          this.model.mobile = referral_invite.customer.contact
                          break
                      }
                    })
                }

                if (this.token) {
                  return this.referralService
                    .getAdvocateSummary(this.merchant_slug, this.advocate_identifier, this.token)
                    .then((summary) => {
                      this.summary = summary
                    })
                }
              })
          } else {
            this.is_enabled = false
          }
        })
        .catch((error) => {
          if (error.status_code === 404) {
            window.location.href = "https://onelocal.com/404"
          } else {
            this.error = error
          }
        })
        .then(() => {
          this.displayService.setApplicationLoaded()
        })
    })
  }

  createAdvocateReferral(form: NgForm, is_invite?: boolean) {
    if (form.valid) {
      const customer_info: AdvocateReferralForm = {
        name: this.model.name,
        email: this.model.email
      }

      if (this.model.mobile) {
        customer_info.mobile = this.toolService.getPhoneBaseValue(this.model.mobile)
      }

      if (this.model.invite_id) {
        customer_info.invite_id = this.model.invite_id
      }

      this.referralService
        .createAdvocateReferral(this.merchant_slug, this.advocate_info.referrer_token, customer_info, is_invite)
        .then(
          (response) => {
            // Keep the name for the success message
            this.referred_friend_name = this.model.name

            // Reset the form for next usage
            form.reset()
            this.error = null

            this.advocate_friend_referred = true

            if (is_invite) {
              this.hide_text_content = true
              return
            }

            const extra_values = {
              "[Friend First Name]": customer_info.name,
              "[Submitted Email]": customer_info.email
            }

            if (response) {
              this.template_type = "friend_gift"

              if (!this.merchant.referral_program.referred_reward.hide_promo_code) {
                this.promo_code = response.friend_promo_code
              }
            } else {
              this.template_type = "friend_confirmation"
            }

            this._setTemplate(this.template_type, extra_values)
          },
          (error) => {
            this.error = error.message
          }
        )
    }
  }

  openReferralProgramCustomUrl() {
    window.open(this.merchant.referral_program.custom_cta.url, "_blank")
  }

  openFacebookUrl() {
    const facebook_advocate_url = `${this.advocate_url}?ref=facebook`
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(facebook_advocate_url)}`, "_blank")
  }

  inviteAnotherFriend() {
    this.advocate_friend_referred = false
    this.hide_text_content = false
  }

  openShareDialog() {
    this.mdDialog.open(ReferralMagicShareModalComponent, {
      data: {
        advocate_info: this.advocate_info,
        advocate_identifier: this.advocate_identifier,
        advocate_url: this.advocate_url,
        colors: this.pages.colors,
        merchant: this.merchant,
        referralService: this.referralService,
        qualification: this.merchant.settings.referral_magic.qualification,
        is_embedded: this.displayService.is_embedded
      }
    })
  }

  openEmailDialog() {
    this.mdDialog.open(ReferralMagicEmailModalComponent, {
      data: {
        advocate_info: this.advocate_info,
        advocate_identifier: this.advocate_identifier,
        advocate_url: this.advocate_url,
        colors: this.pages.colors,
        merchant: this.merchant,
        referralService: this.referralService,
        toolService: this.toolService,
        qualification: this.merchant.settings.referral_magic.qualification,
        is_embedded: this.displayService.is_embedded
      }
    })
  }

  share() {
    if (navigator.share) {
      navigator.share({
        title: "Share Your URL",
        text: this.advocate_url,
        url: this.advocate_url
      })
    }
  }

  shareBySMS() {
    return this.referralService.openSharingSMS(this.merchant, this.advocate_info)
  }

  private _setTemplate(template_type: string, extra_values?: { [search: string]: string }) {
    this.template_type = template_type
    this.template = this.referralService.loadTemplateDynamicValues(
      this.pages.templates[this.template_type],
      this.merchant,
      this.advocate_info,
      extra_values
    )

    if (this.template.show_cover === true) {
      if (this.template.cover_url) {
        this.cover_image = this.template.cover_url
      } else if (this.merchant.referral_program.pages.cover_url) {
        this.cover_image = this.merchant.referral_program.pages.cover_url
      }
    } else {
      this.cover_image = null
    }
  }
}



// WEBPACK FOOTER //
// /home/runner/work/onelocal/onelocal/src/my-referral/src/app/referral-magic/merchant-advocate/merchant-advocate.component.ts